import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { userQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { ErrorResponse } from "@/types/response/ErrorResponse";
import { UserType } from "@/types/user/userType";

const errorResponseSchema: z.ZodType<ErrorResponse> = z.object({ code: z.string() });

export function useResendInvitationMutation() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<AxiosResponse, AxiosError<unknown>, UserType["id"]>({
    mutationFn: (userId) =>
      instance.post(`/api/v2/Users/${userId}/resend-invitation`),
    onSuccess: () => {
      showToast(t("admin.userManagement.resendInvitation.success"), "success");
    },
    onError: (error) => {
      const failed = t("admin.userManagement.resendInvitation.failed");
      const parsed = errorResponseSchema.safeParse(error.response?.data);
      if (parsed.success && parsed.data.code === "USER_ALREADY_ACTIVATED") {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.lists() });
      }
      showToast(
        parsed.success
          ? t(`errorCodes.${parsed.data.code}`, { defaultValue: failed })
          : failed,
        "error",
      );
    },
  });
}
